import { useState, useEffect, useRef } from 'react';
import { syncService } from '../services/SyncService';

const MINIMUM_DISPLAY_MS = 2000; // Show loading screen for at least 2 seconds

// Track if we've already shown the loading screen in this session
let hasCompletedInitialLoad = false;

/**
 * Hook to track initial sync state.
 * Returns isInitialSyncComplete: true only after BOTH:
 * 1. Initial sync has completed
 * 2. Minimum display time (2s) has elapsed
 * 
 * Only shows loading screen once per session (not on route changes).
 */
export function useSyncState() {
    // Start with cached value - if we already completed, skip the loading screen
    const [isReady, setIsReady] = useState(hasCompletedInitialLoad);
    const timerRef = useRef(null);
    const mountTimeRef = useRef(Date.now());

    useEffect(() => {
        // If we've already shown the loading screen this session, we're done
        if (hasCompletedInitialLoad) {
            setIsReady(true);
            return;
        }

        // Function to complete the loading (called when both conditions are met)
        const completeLoading = () => {
            const elapsed = Date.now() - mountTimeRef.current;
            const remainingDelay = Math.max(0, MINIMUM_DISPLAY_MS - elapsed);

            // Clear any existing timer
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = setTimeout(() => {
                hasCompletedInitialLoad = true;
                setIsReady(true);
            }, remainingDelay);
        };

        // Check if sync is already done
        if (syncService.isInitialSyncComplete) {
            completeLoading();
        }

        // Subscribe to sync completion
        const unsubscribe = syncService.onSyncStateChange((complete) => {
            if (complete) {
                completeLoading();
            }
        });

        return () => {
            unsubscribe();
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return { isInitialSyncComplete: isReady };
}

// Reset on logout (call this from logout function)
export function resetSyncState() {
    hasCompletedInitialLoad = false;
}

